import { Widget } from '../core/Widget.js';
import { on, off } from '../core/Events.js';
import Swiper from 'swiper';
import { Autoplay, FreeMode, Grid, EffectFade, Navigation, Pagination, Thumbs } from 'swiper/modules';

/*
Опции SwiperWidget (через data-атрибуты)
----------------------------------------

data-role="main" | "thumbs"
    Режим работы слайдера.
    main — основной слайдер
    thumbs — миниатюры, синхронизируются с основным по data-group.

data-group="gallery1"
    Идентификатор группы для связки main + thumbs.
    Значения должны совпадать.

data-speed="300"
    Скорость анимации.

data-slides-per-view="1"
    Количество видимых слайдов.

data-space-between="16"
    Отступ между слайдами в пикселях.
    (это основной и единственный поддерживаемый атрибут)

data-loop="true"
    Зацикливание.

data-free-mode="true"
    Свободная прокрутка (карусель без фиксации на слайд).

data-watch-slides-progress="true"
    Отслеживание прогресса слайдов.
    (обязательно для thumbs)

data-autoplay-delay="5000"
    Автопрокрутка. Значение — задержка в мс.

data-breakpoints='{ "768": { "slidesPerView": 2 } }'
    Адаптивные настройки. JSON-строка.

data-next-selector=".my-next"
data-prev-selector=".my-prev"
    Кастомные селекторы навигации. По умолчанию используются
    .swiper-button-next и .swiper-button-prev.

data-add-icons="false"
    Отключает автоматическую вставку SVG-иконок в навигационные кнопки.

data-only-mobile="true"
     Показывать свайпер только на мобильных

data-mobile-max="767"
    Максимальная ширина для мобильных


data-grid='{ "rows": 2, "fill": "row" }'

    rows: 2 — две строки
    fill: "row" (или "column") — как “заполнять” сетку
----------------------------------------
*/
/*

Примеры разметки

--------------------------------------------------------
1) Обычный слайдер

<div data-widget="Swiper"
     data-speed="300"
     data-slides-per-view="1"
     data-space-between="16"
     data-autoplay-delay="5000"
     data-add-icons="false"
     >

    <div class="swiper-wrapper">
        <div class="swiper-slide">Слайд 1</div>
        <div class="swiper-slide">Слайд 2</div>
        <div class="swiper-slide">Слайд 3</div>
    </div>

    <div class="swiper-pagination"></div>
    <div class="swiper-button-prev"></div>
    <div class="swiper-button-next"></div>
</div>

--------------------------------------------------------
2) Слайдер с брейкпоинтами

<div data-widget="Swiper"
     data-speed="300"
     data-breakpoints='{
         "768":  { "slidesPerView": 2 },
         "1024": { "slidesPerView": 3 },
         "1440": { "slidesPerView": 4 }
     }'>

    <div class="swiper-wrapper">
        <div class="swiper-slide">1</div>
        <div class="swiper-slide">2</div>
        <div class="swiper-slide">3</div>
        <div class="swiper-slide">4</div>
    </div>

    <div class="swiper-pagination"></div>
</div>

--------------------------------------------------------
3) main + thumbs (галерея)

<!-- Main -->

<div data-widget="Swiper"
     data-role="main"
     data-group="gallery1"
     data-loop="true"
     data-speed="300"
     data-space-between="16">

    <div class="swiper-wrapper">
        <div class="swiper-slide">большой-1</div>
        <div class="swiper-slide">большой-2</div>
        <div class="swiper-slide">большой-3</div>
    </div>

    <div class="swiper-button-prev"></div>
    <div class="swiper-button-next"></div>
    <div class="swiper-pagination"></div>
</div>

<!-- Thumbs -->

<div data-widget="Swiper"
     data-role="thumbs"
     data-group="gallery1"
     data-free-mode="true"
     data-watch-slides-progress="true"
     data-slides-per-view="4">

    <div class="swiper-wrapper">
        <div class="swiper-slide">мини-1</div>
        <div class="swiper-slide">мини-2</div>
        <div class="swiper-slide">мини-3</div>
    </div>
</div>



--------------------------------------------------------
*/
// При необходимости подключить стили в main.css,
// а не здесь:
// import "swiper/css";
// import "swiper/css/navigation";
// import "swiper/css/pagination";
// import "swiper/css/thumbs";
// import "swiper/css/free-mode";
class SwiperWidgetRegistry {
    static register(group, role, instance) {
        const rec = SwiperWidgetRegistry.map.get(group) ?? {};
        rec[role] = instance;
        SwiperWidgetRegistry.map.set(group, rec);
        if (rec.main && rec.thumbs) {
            rec.main.thumbs.swiper = rec.thumbs;
            rec.main.thumbs.init();
            // Важно: после привязки thumbs пересчитываем main,
            // иначе на старте может остаться locked/disabled
            rec.main.update();
            rec.main.navigation?.update?.();
            rec.main.pagination?.update?.();
        }
    }
    static unregister(group, role) {
        const rec = SwiperWidgetRegistry.map.get(group);
        if (!rec)
            return;
        delete rec[role];
        if (!rec.main && !rec.thumbs) {
            SwiperWidgetRegistry.map.delete(group);
        }
        else {
            SwiperWidgetRegistry.map.set(group, rec);
        }
    }
}
SwiperWidgetRegistry.map = new Map();
class SwiperWidget extends Widget {
    constructor() {
        super(...arguments);
        this.swiper = null;
        this._role = "main";
        this._group = "";
        this._onlyMobile = false;
        this._mobileMax = 767;
        this._mediaQuery = null;
        this._swiperOptions = null;
        this._onMediaChange = () => {
            if (!this._mediaQuery) {
                return;
            }
            if (this._mediaQuery.matches) {
                this._initSwiper();
            }
            else {
                this._destroySwiper();
            }
        };
        this._onRefresh = () => {
            if (!this.swiper)
                return;
            this.swiper.update();
            this.swiper.updateSlides();
            this.swiper.updateProgress();
            this.swiper.updateSize();
        };
    }
    _create(options) {
        const host = this.element;
        if (options.implicitSlides === true) {
            this._applyImplicitSlides(host);
        }
        const role = options.role ?? "main";
        const paginationEl = host.querySelector(".swiper-pagination") || undefined;
        // --- Навигация ---
        // Для thumbs навигацию НЕ подключаем по умолчанию,
        // чтобы thumbs не “украл” кнопки у main.
        const canUseNavigation = role !== "thumbs";
        let nextEl;
        let prevEl;
        if (canUseNavigation) {
            const nextSelector = options.nextSelector || ".swiper-button-next";
            const prevSelector = options.prevSelector || ".swiper-button-prev";
            // сначала ищем внутри самого swiper-контейнера
            nextEl = host.querySelector(nextSelector) || undefined;
            prevEl = host.querySelector(prevSelector) || undefined;
            // если селекторы заданы явно — допускаем внешние кнопки (как legacy),
            // но только по явному указанию, чтобы не ловить “случайные” кнопки
            if ((!nextEl && options.nextSelector) || (!prevEl && options.prevSelector)) {
                nextEl =
                    nextEl ||
                        host.parentElement?.querySelector(nextSelector) ||
                        undefined;
                prevEl =
                    prevEl ||
                        host.parentElement?.querySelector(prevSelector) ||
                        undefined;
            }
        }
        const gridRows = options.grid?.rows ?? 1;
        const slidesPerView = options.slidesPerView ??
            (options.role === "thumbs" ? 4 : 1);
        let slidesPerGroup = 1;
        if (options.slidesPerGroup)
            slidesPerGroup = options.slidesPerGroup;
        // Для Grid > 1 строки — листаем “страницами”
        if (gridRows > 1 &&
            slidesPerGroup == null &&
            typeof slidesPerView === "number") {
            slidesPerGroup = slidesPerView * gridRows;
        }
        const autoplay = options.autoplayDelay || options.autoplay
            ? {
                delay: options.autoplayDelay ?? options.autoplay?.delay,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            }
            : undefined;
        const modules = [Autoplay, FreeMode];
        if (gridRows > 1) {
            modules.push(Grid);
        }
        if (options.effect === "fade") {
            modules.push(EffectFade);
        }
        if (nextEl || prevEl) {
            modules.push(Navigation);
        }
        if (paginationEl) {
            modules.push(Pagination);
        }
        if (options.group) {
            modules.push(Thumbs);
        }
        this._swiperOptions = {
            modules,
            loop: options.loop,
            speed: options.speed ?? 300,
            spaceBetween: options.spaceBetween ?? 0,
            slidesPerView,
            slidesPerGroup,
            grid: options.grid,
            freeMode: options.freeMode,
            watchSlidesProgress: options.watchSlidesProgress,
            effect: options.effect,
            fadeEffect: {
                crossFade: !!options.effect
            },
            navigation: canUseNavigation && (nextEl || prevEl)
                ? {
                    nextEl,
                    prevEl,
                    addIcons: options.addIcons || false,
                    //disabledClass: options.navigationDisabledClass,
                }
                : undefined,
            pagination: paginationEl
                ? {
                    el: paginationEl,
                    bulletClass: options.bulletClass || 'swiper-pagination-bullet',
                    bulletActiveClass: options.bulletActiveClass || 'swiper-pagination-bullet-active',
                    clickable: true
                }
                : undefined,
            autoplay,
            breakpoints: options.breakpoints
        };
        this._role = options.role ?? "main";
        this._group = options.group ?? "";
        this._onlyMobile = options.onlyMobile === true;
        this._mobileMax = options.mobileMax ?? 767;
        if (this._onlyMobile) {
            this._initResponsive();
        }
        else {
            this._initSwiper();
        }
        on(this.element, "swiper:refresh", this._onRefresh);
    }
    _initSwiper() {
        if (this.swiper || !this._swiperOptions) {
            return;
        }
        this.swiper = new Swiper(this.element, this._swiperOptions);
        if (this._group) {
            SwiperWidgetRegistry.register(this._group, this._role, this.swiper);
        }
    }
    _destroySwiper() {
        if (!this.swiper) {
            return;
        }
        if (this._group) {
            SwiperWidgetRegistry.unregister(this._group, this._role);
        }
        try {
            this.swiper.destroy(true, true);
        }
        catch {
            /* ignore */
        }
        this.swiper = null;
    }
    _initResponsive() {
        this._mediaQuery = window.matchMedia(`(max-width: ${this._mobileMax}px)`);
        this._mediaQuery.addEventListener("change", this._onMediaChange);
        this._onMediaChange();
    }
    /**
     * Если включён режим implicit slides —
     * прямые потомки `.swiper-wrapper` считаются слайдами.
     */
    _applyImplicitSlides(host) {
        const wrapper = host.querySelector(".swiper-wrapper");
        if (!wrapper)
            return;
        const children = Array.from(wrapper.children);
        for (const el of children) {
            if (!el.classList.contains("swiper-slide")) {
                el.classList.add("swiper-slide");
            }
        }
    }
    _onDestroy() {
        off(this.element, "swiper:refresh", this._onRefresh);
        if (this._mediaQuery) {
            this._mediaQuery.removeEventListener("change", this._onMediaChange);
            this._mediaQuery = null;
        }
        this._destroySwiper();
    }
}
SwiperWidget._alias = "Swiper";

export { SwiperWidget };
